import axios from 'axios'
import {
  planner_plan_path,
  planner_prefill_path,
  planner_preview_path,
  create_planner_plan_path,
  planner_settings_path,
  end_planner_plan_path,
  planner_upcoming_path,
  planner_sessions_path,
  move_planner_session_path,
  swap_planner_session_path,
  swap_preview_planner_session_path,
  skip_planner_session_path,
  complete_planner_session_path,
  unskip_planner_session_path,
  planner_session_path,
  rearrange_planner_session_path,
  lock_planner_session_path,
  planner_session_difficulty_path,
  planner_adjustments_path,
  planner_adjustment_path,
  regenerate_planner_plan_path,
  planner_changes_path,
  undo_planner_change_path,
  apply_planner_proposal_path,
  discard_planner_proposal_path,
  planner_feedback_path,
  planner_devices_path,
  planner_intervals_device_path,
  sync_planner_devices_path,
  test_planner_intervals_device_path,
  planner_stridee_device_path,
  refresh_planner_stridee_device_path
} from 'routes'

// Every write answers { ok, diff, violations, message, change_id, plan } — the
// fresh plan payload rides along so the store never needs a second request.
// A refused operation comes back as HTTP 422 with the same shape; unwrap it.
const unwrap = (promise) =>
  promise
    .then((response) => response.data)
    .catch((error) => {
      if (error.response && error.response.status === 422 && error.response.data && 'ok' in error.response.data) {
        return error.response.data
      }
      throw error
    })

export default {
  load() {
    return axios.get(planner_plan_path()).then((response) => response.data)
  },

  prefill() {
    return axios.get(planner_prefill_path()).then((response) => response.data.prefill)
  },

  // What a setup would commit to; null when the setup is not valid yet.
  preview(payload) {
    return axios
      .post(planner_preview_path(), payload)
      .then((response) => response.data.preview)
      .catch(() => null)
  },

  // The same setup priced at all three approaches, plus what more time buys.
  previewCompare(payload) {
    return axios
      .post(planner_preview_path(), { ...payload, compare: 1 })
      .then((response) => response.data)
      .catch(() => null)
  },

  create(payload) {
    return axios.post(create_planner_plan_path(), payload).then((response) => response.data.plan)
  },

  updateSettings(payload) {
    return unwrap(axios.patch(planner_settings_path(), payload))
  },

  end() {
    return axios.delete(end_planner_plan_path()).then((response) => response.data)
  },

  upcoming() {
    return axios.get(planner_upcoming_path()).then((response) => response.data.upcoming)
  },

  addSession(payload) {
    return unwrap(axios.post(planner_sessions_path(), payload))
  },

  move(id, toDate) {
    return unwrap(axios.post(move_planner_session_path({ id }), { to_date: toDate }))
  },

  swap(id, payload) {
    return unwrap(axios.post(swap_planner_session_path({ id }), payload))
  },

  // { ok, candidate: { title, duration_min, planned_tss, … }, violations } — nothing changes.
  swapPreview(id, params) {
    return unwrap(axios.get(swap_preview_planner_session_path({ id }), { params }))
  },

  skip(id) {
    return unwrap(axios.post(skip_planner_session_path({ id })))
  },

  unskip(id) {
    return unwrap(axios.post(unskip_planner_session_path({ id })))
  },

  // "I rode this": a missed or past session the athlete confirms they did.
  complete(id) {
    return unwrap(axios.post(complete_planner_session_path({ id })))
  },

  remove(id) {
    return unwrap(axios.delete(planner_session_path({ id })))
  },

  rearrange(id) {
    return unwrap(axios.post(rearrange_planner_session_path({ id })))
  },

  lock(id, locked) {
    return unwrap(axios.post(lock_planner_session_path({ id }), { locked }))
  },

  difficulty(id, difficulty, cause) {
    return unwrap(axios.post(planner_session_difficulty_path({ id }), { difficulty, cause }))
  },

  addAdjustment(payload) {
    return unwrap(axios.post(planner_adjustments_path(), payload))
  },

  removeAdjustment(id) {
    return unwrap(axios.delete(planner_adjustment_path({ id })))
  },

  regenerate(scope = 'week') {
    return unwrap(axios.post(regenerate_planner_plan_path(), { scope }))
  },

  changes() {
    return axios.get(planner_changes_path()).then((response) => response.data.changes)
  },

  undo(id) {
    return unwrap(axios.post(undo_planner_change_path({ id })))
  },

  // scope ('week' | 'plan') says how far a realignment reaches.
  applyProposal(id, scope) {
    return unwrap(axios.post(apply_planner_proposal_path({ id }), scope ? { scope } : {}))
  },

  discardProposal(id) {
    return unwrap(axios.post(discard_planner_proposal_path({ id })))
  },

  feedback(context, message) {
    return axios.post(planner_feedback_path(), { context, message }).then((response) => response.data)
  },

  // The week 2 / week 4 survey: three answers and an optional note.
  survey({ week, answers, message }) {
    return axios.post(planner_feedback_path(), { context: 'survey', week, answers, message }).then((response) => response.data)
  },

  // Device sync. Every answer carries { ok, devices }; a rejected key is a 422
  // with the same shape.
  devices() {
    return axios.get(planner_devices_path()).then((response) => response.data.devices)
  },

  connectIntervals(payload) {
    return unwrap(axios.patch(planner_intervals_device_path(), payload))
  },

  disconnectIntervals() {
    return unwrap(axios.delete(planner_intervals_device_path()))
  },

  syncDevices() {
    return unwrap(axios.post(sync_planner_devices_path()))
  },

  // The connection test: one obvious session on tomorrow's calendar, and the
  // rider's answer about whether it reached the device.
  testIntervals() {
    return unwrap(axios.post(test_planner_intervals_device_path()))
  },

  clearIntervalsTest(reached) {
    return unwrap(axios.delete(test_planner_intervals_device_path(), { params: { reached } }))
  },

  // Stridee sends workouts straight to Garmin, Wahoo and COROS. Connecting is
  // a round trip through their hosted page, so this answers with a URL to send
  // the rider to rather than with a connected device.
  connectStridee(provider, returnTo, consent) {
    return unwrap(axios.post(planner_stridee_device_path(), { provider, return_to: returnTo, consent }))
  },

  // Called on the way back: what they actually connected is read from Stridee,
  // never from the redirect.
  refreshStridee() {
    return unwrap(axios.post(refresh_planner_stridee_device_path()))
  },

  // With a provider it lets go of that brand only; without one, of everything.
  disconnectStridee(provider) {
    return unwrap(axios.delete(planner_stridee_device_path(), { params: { provider } }))
  }
}
